import React from 'react';
import { CheckCircleIcon, XCircleIcon, MinusCircleIcon } from 'lucide-react';
interface Scenario {
  id: number;
  name: string;
  revenueTarget: number;
  playMix: Array<{
    play: string;
    allocation: number;
  }>;
  budgetAllocation: number;
  headcount: number;
  timeline: string;
  confidence: number;
}
interface PlanComparisonProps {
  scenarios: Scenario[];
}
export function PlanComparison({
  scenarios
}: PlanComparisonProps) {
  const comparisonScenarios = [{
    ...scenarios[0],
    name: 'Baseline Plan'
  }, {
    id: 2,
    name: 'Aggressive Growth',
    revenueTarget: 15000000,
    playMix: [{
      play: 'High-Intent Account Blitz',
      allocation: 45
    }, {
      play: 'PLG Activation',
      allocation: 30
    }, {
      play: 'Executive Nurture',
      allocation: 15
    }, {
      play: 'Inbound Lead Scoring',
      allocation: 10
    }],
    budgetAllocation: 1275000,
    headcount: 18,
    timeline: '12 months',
    confidence: 65
  }, {
    id: 3,
    name: 'Conservative Approach',
    revenueTarget: 7500000,
    playMix: [{
      play: 'High-Intent Account Blitz',
      allocation: 25
    }, {
      play: 'PLG Activation',
      allocation: 20
    }, {
      play: 'Executive Nurture',
      allocation: 30
    }, {
      play: 'Inbound Lead Scoring',
      allocation: 25
    }],
    budgetAllocation: 637500,
    headcount: 9,
    timeline: '12 months',
    confidence: 88
  }];
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  const getConfidenceIcon = (confidence: number) => {
    if (confidence >= 80) return <CheckCircleIcon className="w-5 h-5 text-green-600" data-id="element-2255" />;
    if (confidence >= 70) return <MinusCircleIcon className="w-5 h-5 text-yellow-600" data-id="element-2256" />;
    return <XCircleIcon className="w-5 h-5 text-red-600" data-id="element-2257" />;
  };
  return <div className="space-y-6" data-id="element-2258">
      <div data-id="element-2259">
        <h2 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2260">
          Side-by-Side Plan Comparison
        </h2>
        <p className="text-sm text-gray-600" data-id="element-2261">
          Compare different scenarios to find the optimal GTM strategy
        </p>
      </div>
      <div className="grid grid-cols-3 gap-6" data-id="element-2262">
        {comparisonScenarios.map(scenario => <div key={scenario.id} className="bg-white rounded-lg border-2 border-gray-200 hover:border-red-500 transition-all" data-id="element-2263">
            {/* Header */}
            <div className="p-6 border-b border-gray-200" data-id="element-2264">
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-2265">
                {scenario.name}
              </h3>
              <div className="flex items-center gap-2" data-id="element-2266">
                {getConfidenceIcon(scenario.confidence)}
                <span className="text-sm font-medium text-gray-600" data-id="element-2267">
                  {scenario.confidence}% Confidence
                </span>
              </div>
            </div>
            {/* Metrics */}
            <div className="p-6 space-y-4" data-id="element-2268">
              {/* Revenue Target */}
              <div data-id="element-2269">
                <p className="text-xs text-gray-600 mb-1" data-id="element-2270">Revenue Target</p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-2271">
                  {formatCurrency(scenario.revenueTarget)}
                </p>
              </div>
              {/* Play Mix */}
              <div data-id="element-2272">
                <p className="text-xs font-semibold text-gray-900 mb-2" data-id="element-2273">
                  Play Mix
                </p>
                <div className="space-y-2" data-id="element-2274">
                  {scenario.playMix.map((play, index) => <div key={index} data-id="element-2275">
                      <div className="flex items-center justify-between mb-1" data-id="element-2276">
                        <span className="text-xs text-gray-700" data-id="element-2277">
                          {play.play}
                        </span>
                        <span className="text-xs font-semibold text-gray-900" data-id="element-2278">
                          {play.allocation}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5" data-id="element-2279">
                        <div className="bg-red-500 h-1.5 rounded-full" style={{
                    width: `${play.allocation}%`
                  }} data-id="element-2280" />
                      </div>
                    </div>)}
                </div>
              </div>
              {/* Budget & Resources */}
              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-200" data-id="element-2281">
                <div data-id="element-2282">
                  <p className="text-xs text-gray-600 mb-1" data-id="element-2283">Budget</p>
                  <p className="text-sm font-bold text-gray-900" data-id="element-2284">
                    {formatCurrency(scenario.budgetAllocation)}
                  </p>
                </div>
                <div data-id="element-2285">
                  <p className="text-xs text-gray-600 mb-1" data-id="element-2286">Headcount</p>
                  <p className="text-sm font-bold text-gray-900" data-id="element-2287">
                    {scenario.headcount} FTEs
                  </p>
                </div>
              </div>
              {/* Timeline */}
              <div className="pt-3 border-t border-gray-200" data-id="element-2288">
                <p className="text-xs text-gray-600 mb-1" data-id="element-2289">Timeline</p>
                <p className="text-sm font-bold text-gray-900" data-id="element-2290">
                  {scenario.timeline}
                </p>
              </div>
            </div>
            {/* Action Button */}
            <div className="p-6 border-t border-gray-200" data-id="element-2291">
              <button className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium" data-id="element-2292">
                Select Plan
              </button>
            </div>
          </div>)}
      </div>
      {/* Comparison Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-2293">
        <table className="w-full" data-id="element-2294">
          <thead className="bg-gray-50 border-b border-gray-200" data-id="element-2295">
            <tr data-id="element-2296">
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-2297">
                Metric
              </th>
              {comparisonScenarios.map(scenario => <th key={scenario.id} className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-2298">
                  {scenario.name}
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200" data-id="element-2299">
            <tr data-id="element-2300">
              <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-2301">
                Revenue Target
              </td>
              {comparisonScenarios.map(scenario => <td key={scenario.id} className="px-6 py-4 text-sm text-gray-700" data-id="element-2302">
                  {formatCurrency(scenario.revenueTarget)}
                </td>)}
            </tr>
            <tr data-id="element-2303">
              <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-2304">
                Budget
              </td>
              {comparisonScenarios.map(scenario => <td key={scenario.id} className="px-6 py-4 text-sm text-gray-700" data-id="element-2305">
                  {formatCurrency(scenario.budgetAllocation)}
                </td>)}
            </tr>
            <tr data-id="element-2306">
              <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-2307">
                Headcount
              </td>
              {comparisonScenarios.map(scenario => <td key={scenario.id} className="px-6 py-4 text-sm text-gray-700" data-id="element-2308">
                  {scenario.headcount} FTEs
                </td>)}
            </tr>
            <tr data-id="element-2309">
              <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-2310">
                Confidence Score
              </td>
              {comparisonScenarios.map(scenario => <td key={scenario.id} className="px-6 py-4 text-sm text-gray-700" data-id="element-2311">
                  <div className="flex items-center gap-2" data-id="element-2312">
                    {getConfidenceIcon(scenario.confidence)}
                    <span data-id="element-2313">{scenario.confidence}%</span>
                  </div>
                </td>)}
            </tr>
          </tbody>
        </table>
      </div>
    </div>;
}